import type { ChatMessage } from '../../../shared/types';
import type { ChatAdapterConfig, ChatProtocolAdapter, ChatAdapterStatus } from './types';
import { generateAnonymousNick, sanitizeNick, senderColor } from './nick';

/**
 * Minimal zero-dependency Nostr client adapter (NIP-53 kind 1311 Live Chat) over native WebSocket.
 */
export class NostrAdapter implements ChatProtocolAdapter {
  private ws: WebSocket | null = null;
  private subId: string = '';
  private spaceTag: string = '';
  private isManuallyClosed: boolean = false;
  private seenEventIds: Set<string> = new Set();

  currentNick: string = '';
  onMessage: (msg: ChatMessage) => void = () => {};
  onStatus: (status: ChatAdapterStatus, detail?: string) => void = () => {};
  onError: (err: Error) => void = () => {};

  async connect(config: ChatAdapterConfig): Promise<void> {
    this.isManuallyClosed = false;
    this.seenEventIds.clear();

    const initialNick = config.nick ? sanitizeNick(config.nick) : generateAnonymousNick();
    this.currentNick = initialNick;

    // Relay URL: default to fountain relay if not provided
    let rawRelay = config.relayUrl || config.server || 'wss://relay.fountain.fm';
    rawRelay = rawRelay.trim();
    if (!rawRelay.startsWith('wss://') && !rawRelay.startsWith('ws://')) {
      rawRelay = `wss://${rawRelay.replace(/^\/+/, '')}`;
    }
    const relayUrl = rawRelay;
    // Space tag or channel (NIP-53 "a" tag coordinate)
    this.spaceTag = config.space || config.channel || '';

    // Generate unique subscription ID
    this.subId = `sub_${Math.random().toString(36).slice(2, 10)}`;

    this.onStatus('connecting');

    return new Promise<void>((resolve, reject) => {
      try {
        const ws = new WebSocket(relayUrl);
        this.ws = ws;
        let resolved = false;

        ws.onopen = () => {
          this.onStatus('connected');
          if (!resolved) {
            resolved = true;
            resolve();
          }

          // Subscribe via ["REQ", subId, filter]
          const filter: Record<string, any> = {
            kinds: [1311],
            limit: 50,
          };
          if (this.spaceTag) {
            filter['#a'] = [this.spaceTag];
          }

          const reqMessage = JSON.stringify(['REQ', this.subId, filter]);
          ws.send(reqMessage);
        };

        ws.onmessage = (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);
            this.handleNostrMessage(data);
          } catch (e) {
            // Ignore non-json frames
          }
        };

        ws.onerror = () => {
          const err = new Error('Nostr WebSocket error');
          this.onError(err);
          this.onStatus('error', 'Nostr WebSocket error');
          if (!resolved) {
            resolved = true;
            reject(err);
          }
        };

        ws.onclose = () => {
          if (!this.isManuallyClosed) {
            this.onStatus('disconnected');
          }
        };
      } catch (err: any) {
        this.onError(err);
        this.onStatus('error', err?.message || 'Failed to connect');
        reject(err);
      }
    });
  }

  async sendMessage(text: string): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Not connected to Nostr relay');
    }
    const cleanText = text.trim();
    if (!cleanText) return;

    // Build kind 1311 event
    // Synthetic pubkey based on currentNick if unauthenticated
    const pubkey = `anon_${this.currentNick}`;
    const tags: string[][] = [];
    if (this.spaceTag) {
      tags.push(['a', this.spaceTag]);
    }

    const event = {
      id: `nostr-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      pubkey,
      created_at: Math.floor(Date.now() / 1000),
      kind: 1311,
      tags,
      content: cleanText,
      sig: '',
    };

    // Send EVENT if supported by relay
    this.ws.send(JSON.stringify(['EVENT', event]));

    // Emit local ChatMessage immediately
    const localMsg: ChatMessage = {
      id: event.id,
      text: cleanText,
      displayName: this.currentNick,
      timestamp: Date.now(),
      protocol: 'nostr',
      senderColor: senderColor(this.currentNick),
    };
    this.onMessage(localMsg);
  }

  async changeNick(newNick: string): Promise<void> {
    this.currentNick = sanitizeNick(newNick);
  }

  disconnect(): void {
    this.isManuallyClosed = true;
    if (this.ws) {
      try {
        if (this.ws.readyState === WebSocket.OPEN && this.subId) {
          this.ws.send(JSON.stringify(['CLOSE', this.subId]));
        }
        this.ws.close();
      } catch {
        // Ignore errors during close
      }
      this.ws = null;
    }
    this.onStatus('disconnected');
  }

  private handleNostrMessage(data: any): void {
    if (!Array.isArray(data)) return;
    const [type, , payload] = data;

    // ["EVENT", subId, event]
    if (type === 'EVENT' && payload && typeof payload === 'object') {
      const event = payload;
      if (event.id && this.seenEventIds.has(event.id)) {
        return;
      }
      if (event.id) {
        this.seenEventIds.add(event.id);
      }

      // Format displayName from pubkey or tags if available
      const pubkey = event.pubkey || 'anonymous';
      const shortPubkey = pubkey.length > 12 ? `${pubkey.slice(0, 8)}...` : pubkey;

      const msg: ChatMessage = {
        id: event.id || `nostr-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        text: event.content || '',
        displayName: shortPubkey,
        timestamp: event.created_at ? event.created_at * 1000 : Date.now(),
        protocol: 'nostr',
        senderColor: senderColor(pubkey),
      };

      this.onMessage(msg);
    }
  }
}
